// ./components/QueueScreen/MobileQueueScreen.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { useParams } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import socket from "../../socket";
import { apiFetch } from "../../utils/api";

/** ---------------------- utils ---------------------- */
function normTicketId(v) {
  return v ? String(v).split("-")[0] : null;
}
function stableKeyFrom(p) {
  if (p?.order_id != null) return String(p.order_id);
  const rootTid = normTicketId(p?.ticket_instance_id);
  return rootTid || null;
}
function findMatchingTicketKey(tickets, p) {
  const preferred = stableKeyFrom(p);
  if (!preferred) return null;
  const rootTid = normTicketId(p?.ticket_instance_id);
  const byOrder = tickets.find(t => t.order_id != null && String(t.order_id) === preferred);
  if (byOrder) return byOrder.ticket_instance_id;
  const byRoot = tickets.find(t => normTicketId(t.ticket_instance_id) === rootTid);
  return byRoot ? byRoot.ticket_instance_id : preferred;
}
function statusOfTicket(t) {
  if (t.servedAt) return "Finished";
  if (t.readyToServe) return "Ready To Serve";
  if ((t.bumpedCount ?? 0) > 0 || t.anyCourseBumped) return "In Progress";
  return "Received";
}
function statusColors(status, isDark) {
  const key = String(status).toLowerCase();
  const map = {
    received: isDark ? "bg-sky-900/40 border-sky-700" : "bg-sky-50 border-sky-200",
    "in progress": isDark ? "bg-amber-600/35 border-amber-400" : "bg-amber-100 border-amber-300",
    "ready to serve": isDark ? "bg-emerald-900/40 border-emerald-700" : "bg-emerald-50 border-emerald-200",
    finished: isDark ? "bg-gray-800 border-gray-600 animate-pulse-slow" : "bg-gray-200 border-gray-300 animate-pulse-slow",
    served: isDark ? "bg-gray-800 border-gray-600 animate-pulse" : "bg-gray-200 border-gray-300 animate-pulse"
  };
  return map[key] || (isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200");
}
function normalizeGuestValue(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return normalizeGuestValue(parsed);
    } catch {
      return trimmed;
    }
  }
  if (typeof value === "object") {
    const candidates = [value.name, value.full_name, value.fullName, value.display_name, value.displayName, value.member_name, value.memberName, value.guest_name, value.guestName, value.value];
    for (const candidate of candidates) {
      const normalized = normalizeGuestValue(candidate);
      if (normalized) return normalized;
    }
  }
  return String(value).trim() || null;
}
function normalizeHeaderFields(payload = {}) {
  return {
    user_info: normalizeGuestValue(payload.user_info ?? payload.UserInfo ?? payload.guest_name ?? payload.guestName ?? null),
    table_name: payload.table_name ?? payload.GuestTable ?? payload.table ?? null,
    server_name: payload.server_name ?? payload.ServerName ?? payload.server ?? null,
    destination: payload.destination ?? payload.Destination ?? null
  };
}
function deriveSeat(it) {
  const direct = it?.seat_number ?? it?.Seat ?? it?.SeatNumber ?? it?.seat ?? it?.PreModifier?.S0 ?? it?.PreModifier?.S1;
  const fromDirect = String(direct ?? "").trim();
  if (fromDirect) {
    const match = fromDirect.match(/(\d+)/);
    if (match) return match[1];
  }
  const pm = it?.PreModifier || {};
  for (const key of ["S0", "S1", "Seat", "SeatNumber"]) {
    const value = String(pm?.[key] ?? "").trim();
    if (!value) continue;
    const match = value.match(/(\d+)/);
    if (match) return match[1];
  }
  const conds = Array.isArray(it?.Condiment) ? it.Condiment : it?.Condiment ? [it.Condiment] : [];
  for (const condiment of conds) {
    const match = String(condiment?.Name || "").match(/SEAT\s*#?\s*(\d+)/i);
    if (match) return match[1];
  }
  return null;
}
const RUNNER_NAME = "Runner";
export default function MobileQueueScreen() {
  const {
    sublocationId
  } = useParams();
  const {
    isDark,
    toggleDarkMode
  } = useDarkMode("mobileQueueDarkMode");
  const [tickets, setTickets] = useState([]);
  const servedTimersRef = useRef(new Map());

  // Expo item index: Map<ticketKey, Map<course, Set<item_id>>>
  const itemsIndexRef = useRef(new Map());
  // Item metadata: Map<ticketKey, Map<item_id, {name, seat_number, course}>>
  const itemsMetaRef = useRef(new Map());
  // Bumped index (for cross-outs): Map<ticketKey, Map<course, Set<item_id>>>
  const bumpedIndexRef = useRef(new Map());
  // If EXPO bump-ticket’ed, future items should auto-bump: Map<ticketKey, boolean>
  const allItemsBumpedRef = useRef(new Map());

  // --- notifications state/refs ---
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const notifiedRef = useRef(new Map()); // Map<ticketKey, Set<courseNum>> to dedupe
  const prevReadyMapRef = useRef(new Map()); // Map<ticketKey, Set<courseNum>> snapshot for diffing
  const [foodRunnerView, setFoodRunnerView] = useState(false);
  async function ensureNotificationPermission() {
    if (typeof Notification === "undefined") return false;
    if (Notification.permission === "granted") {
      setNotifPermission("granted");
      return true;
    }
    if (Notification.permission === "denied") {
      setNotifPermission("denied");
      return false;
    }
    try {
      const res = await Notification.requestPermission();
      setNotifPermission(res);
      return res === "granted";
    } catch {
      return false;
    }
  }
  async function getSWRegistration() {
    if (!("serviceWorker" in navigator)) return null;
    try {
      const reg = await navigator.serviceWorker.ready;
      return reg || null;
    } catch {
      return null;
    }
  }
  async function fireNotification({
    title,
    body,
    tag,
    data
  }) {
    const ok = await ensureNotificationPermission();
    if (!ok) return;
    const reg = await getSWRegistration();
    try {
      if (reg?.showNotification) {
        await reg.showNotification(title, {
          body,
          tag,
          renotify: true,
          icon: "/icons/icon-192x192.png",
          badge: "/icons/badge-72x72.png",
          data
        });
        return;
      }
    } catch {/* fall through */}
    try {
      new Notification(title, {
        body,
        tag
      });
    } catch {/* ignore */}
  }

  // --- server selection state ---
  const [servers, setServers] = useState([]);
  const [serversLoading, setServersLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [expoStationId, setExpoStationId] = useState(null);
  const [selectedServer, setSelectedServer] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("qs:selectedServer") || "";
    }
    return "";
  });
  const seenOrderReceivedRef = useRef(new Map());
  const within = (ms, ts) => typeof ts === "number" && Date.now() - ts < ms;

  /** ---------- ticket summary + totals ---------- */
  const ensureTicketSummary = payload => {
    setTickets(prev => {
      const key = findMatchingTicketKey(prev, payload);
      if (!key) return prev;
      const idx = prev.findIndex(t => t.ticket_instance_id === key);
      const now = new Date().toISOString();
      const rawCovers = payload.covers;
      const covers = rawCovers == null ? null : /^\s*\d+\s*$/.test(String(rawCovers)) ? String(rawCovers).trim() : /^covers:\s*(\d+)/i.exec(String(rawCovers))?.[1] ?? String(rawCovers).trim();
      const msgEntries = Array.isArray(payload.ticket_messages) ? payload.ticket_messages.map(m => String(m)) : payload.ticket_messages && typeof payload.ticket_messages === "object" ? Object.values(payload.ticket_messages).map(m => String(m)) : [];

      // ensure courseRanBy map exists
      const ensureRanBy = obj => obj && typeof obj === "object" ? obj : {};
      if (idx >= 0) {
        const cur = prev[idx];
        const merged = {
          ...cur,
          order_id: payload.order_id ?? cur.order_id ?? null,
          user_info: payload.user_info ?? cur.user_info ?? null,
          table_name: payload.table_name ?? cur.table_name ?? null,
          server_name: payload.server_name ?? cur.server_name ?? null,
          covers: covers ?? cur.covers ?? null,
          createdAt: cur.createdAt ?? (payload.received_at || now),
          ticketMessages: (Array.isArray(cur.ticketMessages) ? cur.ticketMessages : []).concat(msgEntries).slice(-10),
          bumpedCount: cur.bumpedCount ?? 0,
          totalItems: cur.totalItems ?? 0,
          courseTotals: cur.courseTotals ?? {},
          courseReady: cur.courseReady || {},
          courseServed: cur.courseServed || {},
          courseRanBy: ensureRanBy(cur.courseRanBy),
          anyCourseBumped: !!cur.anyCourseBumped,
          readyToServe: !!cur.readyToServe
        };
        return [...prev.slice(0, idx), merged, ...prev.slice(idx + 1)];
      }
      return [...prev, {
        ticket_instance_id: key,
        order_id: payload.order_id ?? null,
        table_name: payload.table_name ?? null,
        server_name: payload.server_name ?? null,
        destination: null,
        user_info: payload.user_info ?? null,
        covers,
        createdAt: payload.received_at || now,
        finishedAt: null,
        servedAt: null,
        ticketMessages: msgEntries.slice(-10),
        activityLog: [],
        bumpedCount: 0,
        totalItems: 0,
        courseTotals: {},
        courseReady: {},
        courseServed: {},
        courseRanBy: {},
        anyCourseBumped: false,
        readyToServe: false
      }];
    });
  };
  const recomputeTotalsFromIndex = ticketKey => {
    const courseMap = itemsIndexRef.current.get(ticketKey) || new Map();
    const courseTotals = {};
    let total = 0;
    Array.from(courseMap.entries()).sort((a, b) => Number(a[0]) - Number(b[0])).forEach(([c, set]) => {
      const n = set?.size ?? 0;
      courseTotals[Number(c)] = n;
      total += n;
    });
    setTickets(prev => prev.map(t => t.ticket_instance_id === ticketKey ? {
      ...t,
      totalItems: total,
      courseTotals
    } : t));
  };
  const recomputeBumpedTotals = ticketKey => {
    const bumpMap = bumpedIndexRef.current.get(ticketKey) || new Map();
    let bumped = 0;
    for (const set of bumpMap.values()) bumped += set.size || 0;
    setTickets(prev => prev.map(t => t.ticket_instance_id === ticketKey ? {
      ...t,
      bumpedCount: bumped
    } : t));
  };
  const courseList = (courseTotals = {}) => Object.entries(courseTotals).map(([c, n]) => ({
    c: Number(c),
    n: Number(n)
  })).sort((a, b) => a.c - b.c);

  // EXPO marks course ready => set per-course ready and ticket readyToServe if any course ready
  function markCourseReady(ticketKey, courseNum, ready = true) {
    setTickets(prev => prev.map(t => {
      if (t.ticket_instance_id !== ticketKey) return t;
      const courseReady = {
        ...(t.courseReady || {})
      };
      if (ready) courseReady[Number(courseNum)] = true;else delete courseReady[Number(courseNum)];
      const anyReady = Object.keys(courseReady).length > 0;
      return {
        ...t,
        courseReady,
        anyCourseBumped: true,
        readyToServe: anyReady
      };
    }));
  }

  // mark who ran (picked up) a course
  function markCourseRanBy(ticketKey, courseNum, serverName) {
    setTickets(prev => prev.map(t => {
      if (t.ticket_instance_id !== ticketKey) return t;
      const courseRanBy = {
        ...(t.courseRanBy || {})
      };
      courseRanBy[Number(courseNum)] = String(serverName || "").trim();
      return {
        ...t,
        courseRanBy
      };
    }));
  }
  function getNextReadyCourse(t) {
    const ready = Object.keys(t.courseReady || {}).map(Number).sort((a, b) => a - b);
    return ready.length ? ready[0] : null;
  }
  function markPickedUpOne(ticketKey) {
    setTickets(prev => {
      return prev.map(t => {
        if (t.ticket_instance_id !== ticketKey) return t;
        const nextCourse = getNextReadyCourse(t);
        if (nextCourse == null) return t;
        const courseReady = {
          ...(t.courseReady || {})
        };
        delete courseReady[nextCourse];
        const served = {
          ...(t.courseServed || {})
        };
        served[nextCourse] = true;
        const totalCourses = Object.keys(t.courseTotals || {}).length;
        const servedCount = Object.keys(served).filter(k => k in (t.courseTotals || {})).length;
        const fullyServed = totalCourses > 0 && servedCount >= totalCourses;
        if (fullyServed) {
          return {
            ...t,
            courseReady,
            courseServed: served,
            readyToServe: false,
            servedAt: new Date().toISOString()
          };
        }
        return {
          ...t,
          courseReady,
          courseServed: served,
          readyToServe: Object.keys(courseReady).length > 0
        };
      });
    });

    // schedule removal after pulse if fully served
    setTimeout(() => {
      setTickets(prev => {
        const cur = prev.find(x => x.ticket_instance_id === ticketKey);
        if (!cur || !cur.servedAt) return prev;
        if (servedTimersRef.current.has(ticketKey)) {
          clearTimeout(servedTimersRef.current.get(ticketKey));
        }
        const to = setTimeout(() => {
          setTickets(p2 => p2.filter(x => x.ticket_instance_id !== ticketKey));
          servedTimersRef.current.delete(ticketKey);
        }, 5_000);
        servedTimersRef.current.set(ticketKey, to);
        return prev;
      });
    }, 0);
  }

  /** ---------- fetch servers for first-run modal ---------- */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setServersLoading(true);
        setServerError("");
        const resp = await apiFetch("/orders/servers");
        if (!resp.ok) {
          const txt = await resp.text().catch(() => "");
          throw new Error(`HTTP ${resp.status} ${txt}`);
        }
        const rows = await resp.json(); // [{ server_name: "..." }, ...]
        const list = Array.isArray(rows) ? rows.map(r => String(r.server_name || "").trim()).filter(Boolean) : [];
        const uniq = Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
        if (alive) setServers([RUNNER_NAME, ...uniq]); // inject Runner
      } catch (e) {
        if (alive) {
          setServerError("Unable to load servers.");
          setServers([RUNNER_NAME]); // at least Runner is available
        }
        console.warn("Failed to load servers:", e);
      } finally {
        if (alive) setServersLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    if (!sublocationId) return;
    let active = true;
    (async () => {
      try {
        const stationsRes = await apiFetch(`/updates-reports/stations?sublocation_id=${encodeURIComponent(sublocationId)}`);
        const stations = stationsRes.ok ? await stationsRes.json() : [];
        if (!active) return;
        let nextExpoStationId = null;
        for (const row of Array.isArray(stations) ? stations : []) {
          const stationId = Number(row?.station_id);
          const role = String(row?.role || "").trim().toLowerCase();
          if (!Number.isFinite(stationId) || role !== "expo") continue;
          if (!Number.isFinite(nextExpoStationId) || stationId < nextExpoStationId) {
            nextExpoStationId = stationId;
          }
        }
        setExpoStationId(Number.isFinite(nextExpoStationId) ? nextExpoStationId : null);
      } catch (err) {
        console.error("Failed to load Mobile Queue Screen stations:", err);
        if (active) setExpoStationId(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [sublocationId]);
  const handlePickServer = name => {
    setSelectedServer(name);
    try {
      localStorage.setItem("qs:selectedServer", name);
    } catch {}
  };
  function hydrateQueueFromRows(rows = [], queueEvents = []) {
    const nextItemsIndex = new Map();
    const nextItemsMeta = new Map();
    const nextBumpedIndex = new Map();
    const nextAllItemsBumped = new Map();
    const nextTickets = [];
    const queueEventsByOrder = new Map();
    for (const evt of Array.isArray(queueEvents) ? queueEvents : []) {
      const orderKey = stableKeyFrom(evt);
      if (!orderKey) continue;
      const list = queueEventsByOrder.get(orderKey) || [];
      list.push(evt);
      queueEventsByOrder.set(orderKey, list);
    }
    const isDisplayRealItem = item => {
      const name = String(item?.Name || item?.name || "").trim();
      const category = String(item?.Category || item?.category || "").trim();
      const status = String(item?.status || "").toLowerCase();
      if (!name) return false;
      if (category === "Course Separator") return false;
      if (/^\*{3,}.*\*{3,}$/.test(name)) return false;
      if (name === "* MESSAGE *") return false;
      if (name === "------ FIRE ------") return false;
      if (Number(item?.TransType) === 2 || status === "voided") return false;
      return true;
    };
    for (const row of rows) {
      const key = stableKeyFrom(row);
      if (!key) continue;
      const rowStatus = String(row?.status || "").toLowerCase();
      if (rowStatus === "voided") continue;
      const ticket = typeof row?.ticket_json === "string" ? (() => {
        try {
          return JSON.parse(row.ticket_json);
        } catch {
          return null;
        }
      })() : row?.ticket_json ?? null;
      const normalizedHeaders = normalizeHeaderFields({
        ...ticket,
        ...row,
        user_info: row?.user_info ?? ticket?.UserInfo ?? ticket?.user_info ?? row?.UserInfo ?? null,
        guest_name: row?.guest_name ?? ticket?.guest_name ?? ticket?.guestName ?? row?.guestName ?? null
      });
      const rawItems = Array.isArray(ticket?.Item) ? ticket.Item : ticket?.Item ? [ticket.Item] : [];
      const items = rawItems.filter(isDisplayRealItem);
      if (items.length === 0) continue;
      const courseMap = new Map();
      const bumpMap = new Map();
      const metaMap = new Map();
      const courseReady = {};
      let bumpedCount = 0;
      for (const item of items) {
        const itemId = String(item?.ID || "").trim();
        if (!itemId) continue;
        const courseNum = Number.isFinite(Number(item?.course)) ? Number(item.course) : 0;
        if (!courseMap.has(courseNum)) courseMap.set(courseNum, new Set());
        courseMap.get(courseNum).add(itemId);
        metaMap.set(itemId, {
          name: String(item?.Name || item?.name || itemId),
          seat_number: (() => {
            const seat = deriveSeat(item);
            return seat != null ? String(seat) : null;
          })(),
          course: courseNum
        });
        const itemStatus = String(item?.status || "").toLowerCase();
        if (itemStatus === "bumped" || item?.isBumped) {
          if (!bumpMap.has(courseNum)) bumpMap.set(courseNum, new Set());
          bumpMap.get(courseNum).add(itemId);
          bumpedCount += 1;
        }
      }
      for (const [courseNum, itemSet] of courseMap.entries()) {
        const itemCount = itemSet?.size || 0;
        const bumpedForCourse = (bumpMap.get(courseNum) || new Set()).size;
        if (itemCount > 0 && bumpedForCourse >= itemCount) {
          courseReady[Number(courseNum)] = true;
        }
      }
      const courseDisplayBoxes = row?.course_bump_display_boxes && typeof row.course_bump_display_boxes === "object" ? row.course_bump_display_boxes : {};
      for (const [courseKey, boxes] of Object.entries(courseDisplayBoxes)) {
        const courseNum = Number(courseKey);
        if (!Number.isFinite(courseNum)) continue;
        const expoBox = (Array.isArray(boxes) ? boxes : []).find(box => Number(box?.station_id) === 1);
        if (expoBox?.bumped) {
          courseReady[courseNum] = true;
        }
      }
      const totalItems = Array.from(courseMap.values()).reduce((sum, set) => sum + (set?.size || 0), 0);
      const courseTotals = Object.fromEntries(Array.from(courseMap.entries()).sort((a, b) => Number(a[0]) - Number(b[0])).map(([courseNum, set]) => [courseNum, set.size]));
      nextItemsIndex.set(key, courseMap);
      nextItemsMeta.set(key, metaMap);
      nextBumpedIndex.set(key, bumpMap);
      if (rowStatus === "bumped") {
        nextAllItemsBumped.set(key, true);
      }
      const hydratedTicket = {
        ticket_instance_id: key,
        raw_ticket_instance_id: row?.ticket_instance_id ?? null,
        order_id: row?.order_id ?? null,
        table_name: normalizedHeaders.table_name ?? null,
        server_name: normalizedHeaders.server_name ?? null,
        destination: normalizedHeaders.destination ?? null,
        user_info: normalizedHeaders.user_info ?? null,
        covers: ticket?.OrderMessages?.covers ?? null,
        createdAt: row?.first_received_at || row?.received_at || new Date().toISOString(),
        finishedAt: null,
        servedAt: null,
        ticketMessages: [],
        activityLog: [],
        bumpedCount,
        totalItems,
        courseTotals,
        courseReady,
        courseServed: {},
        courseRanBy: {},
        anyCourseBumped: bumpedCount > 0,
        readyToServe: Object.keys(courseReady).length > 0
      };
      const runEvents = queueEventsByOrder.get(key) || [];
      if (runEvents.length > 0) {
        const courseServed = {
          ...(hydratedTicket.courseServed || {})
        };
        const courseRanBy = {
          ...(hydratedTicket.courseRanBy || {})
        };
        const nextCourseReady = {
          ...(hydratedTicket.courseReady || {})
        };
        let latestServedAt = hydratedTicket.servedAt;
        for (const evt of runEvents) {
          if (String(evt?.event_type || "").toLowerCase() !== "course_ran") continue;
          const courseNum = Number(evt?.course);
          if (!Number.isFinite(courseNum)) continue;
          courseServed[courseNum] = true;
          delete nextCourseReady[courseNum];
          const who = String(evt?.ran_by || "").trim();
          if (who) courseRanBy[courseNum] = who;
          const eventAt = evt?.event_at || evt?.created_at || null;
          if (eventAt && (!latestServedAt || new Date(eventAt) > new Date(latestServedAt))) {
            latestServedAt = eventAt;
          }
        }
        hydratedTicket.courseServed = courseServed;
        hydratedTicket.courseRanBy = courseRanBy;
        hydratedTicket.courseReady = nextCourseReady;
        hydratedTicket.readyToServe = Object.keys(nextCourseReady).length > 0;
        const totalCourses = Object.keys(hydratedTicket.courseTotals || {}).length;
        const servedCount = Object.keys(courseServed).filter(courseKey => courseKey in (hydratedTicket.courseTotals || {})).length;
        if (totalCourses > 0 && servedCount >= totalCourses) {
          hydratedTicket.servedAt = latestServedAt || new Date().toISOString();
        }
      }
      if (hydratedTicket.servedAt) continue;
      nextTickets.push(hydratedTicket);
    }
    itemsIndexRef.current = nextItemsIndex;
    itemsMetaRef.current = nextItemsMeta;
    bumpedIndexRef.current = nextBumpedIndex;
    allItemsBumpedRef.current = nextAllItemsBumped;
    setTickets(nextTickets);
  }

  /** ---------- SOCKETS ---------- */
  useEffect(() => {
    if (!sublocationId) return undefined;
    const payload = {
      sublocationId: Number(sublocationId)
    };
    const joinRoom = () => socket.emit("queue-screen:join", payload);
    joinRoom();
    socket.on("connect", joinRoom);
    return () => {
      socket.off("connect", joinRoom);
      socket.emit("queue-screen:leave", payload);
    };
  }, [sublocationId]);
  useEffect(() => {
    const onConnect = () => {
      if (!expoStationId) return;
      (async () => {
        try {
          const [activeRes, bumpedRes, queueEventsRes] = await Promise.all([apiFetch(`/orders?station_id=${encodeURIComponent(expoStationId)}`), apiFetch(`/orders/bumped-today?station_id=${encodeURIComponent(expoStationId)}`), apiFetch(`/orders/queue-screen-events?sublocation_id=${encodeURIComponent(sublocationId)}`)]);
          const activeRows = activeRes.ok ? await activeRes.json() : [];
          const bumpedRows = bumpedRes.ok ? await bumpedRes.json() : [];
          const queueEvents = queueEventsRes.ok ? await queueEventsRes.json() : [];
          const mergedByOrder = new Map();
          for (const row of [...(Array.isArray(activeRows) ? activeRows : []), ...(Array.isArray(bumpedRows) ? bumpedRows : [])]) {
            const key = stableKeyFrom(row);
            if (!key) continue;
            mergedByOrder.set(key, row);
          }
          hydrateQueueFromRows(Array.from(mergedByOrder.values()), queueEvents);
        } catch (err) {
          console.error("Failed to hydrate Mobile Queue Screen from DB:", err);
        }
      })();
    };
    const onItemReceived = p => {
      const role = (p?.sender_role || "").toLowerCase();
      const tk = stableKeyFrom(p);
      const itemId = String(p.item_id ?? "");
      if (!tk) return;
      ensureTicketSummary({
        ...p,
        ticket_instance_id: tk
      });
      if (!itemId) return;

      // Only EXPO defines the master item index
      if (role === "expo") {
        let courseNum = Number(p.course);
        if (!Number.isFinite(courseNum) || courseNum < 0) courseNum = 0;

        // Index
        let courseMap = itemsIndexRef.current.get(tk);
        if (!courseMap) {
          courseMap = new Map();
          itemsIndexRef.current.set(tk, courseMap);
        }
        let set = courseMap.get(courseNum);
        if (!set) {
          set = new Set();
          courseMap.set(courseNum, set);
        }
        if (!set.has(itemId)) {
          set.add(itemId);
          recomputeTotalsFromIndex(tk);
        }

        // Metadata
        let metaMap = itemsMetaRef.current.get(tk);
        if (!metaMap) {
          metaMap = new Map();
          itemsMetaRef.current.set(tk, metaMap);
        }
        const name = String(p.item_name ?? p.name ?? p.menu_name ?? itemId);
        const seat_number = (() => {
          const seat = deriveSeat(p);
          return seat != null ? String(seat) : null;
        })();
        metaMap.set(itemId, {
          name,
          seat_number,
          course: courseNum
        });

        // Auto-bump new items if whole ticket was bumped previously
        if (allItemsBumpedRef.current.get(tk)) {
          let bumpMap = bumpedIndexRef.current.get(tk);
          if (!bumpMap) {
            bumpMap = new Map();
            bumpedIndexRef.current.set(tk, bumpMap);
          }
          let bset = bumpMap.get(courseNum);
          if (!bset) {
            bset = new Set();
            bumpMap.set(courseNum, bset);
          }
          if (!bset.has(itemId)) {
            bset.add(itemId);
            recomputeBumpedTotals(tk);
          }
        }
      }
    };
    const onOrderReceived = p => {
      const unifiedKey = stableKeyFrom(p);
      if (!unifiedKey) return;
      const last = seenOrderReceivedRef.current.get(unifiedKey);
      if (within(5000, last)) return;
      seenOrderReceivedRef.current.set(unifiedKey, Date.now());
      ensureTicketSummary(p);
      setTickets(prev => prev.map(t => {
        if (t.ticket_instance_id !== unifiedKey) return t;
        const msg = `Received · ${new Date(p.received_at || Date.now()).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })}`;
        const log = [{
          ts: Date.now(),
          msg
        }, ...(t.activityLog || [])].slice(0, 10);
        return {
          ...t,
          activityLog: log
        };
      }));
    };
    const onBumpItem = p => {
      const tk = stableKeyFrom(p);
      const role = (p?.sender_role || "").toLowerCase();
      const itemId = String(p.item_id ?? "");
      const isBumped = !!p.isBumped;
      if (!tk) return;
      ensureTicketSummary({
        ...p,
        ticket_instance_id: tk
      });
      if (!itemId) return;
      if (role === "expo") {
        let courseNum = Number(p.course);
        if (!Number.isFinite(courseNum) || courseNum < 0) courseNum = 0;
        let courseMap = itemsIndexRef.current.get(tk);
        if (!courseMap) {
          courseMap = new Map();
          itemsIndexRef.current.set(tk, courseMap);
        }
        let set = courseMap.get(courseNum);
        if (!set) {
          set = new Set();
          courseMap.set(courseNum, set);
        }
        if (!set.has(itemId)) {
          set.add(itemId);
          recomputeTotalsFromIndex(tk);
        }
        let metaMap = itemsMetaRef.current.get(tk);
        if (!metaMap) {
          metaMap = new Map();
          itemsMetaRef.current.set(tk, metaMap);
        }
        if (!metaMap.has(itemId)) {
          const name = String(p.item_name ?? p.name ?? p.menu_name ?? itemId);
          const seat_number = (() => {
            const seat = deriveSeat(p);
            return seat != null ? String(seat) : null;
          })();
          metaMap.set(itemId, {
            name,
            seat_number,
            course: courseNum
          });
        }
        let bumpMap = bumpedIndexRef.current.get(tk);
        if (!bumpMap) {
          bumpMap = new Map();
          bumpedIndexRef.current.set(tk, bumpMap);
        }
        let bset = bumpMap.get(courseNum);
        if (!bset) {
          bset = new Set();
          bumpMap.set(courseNum, bset);
        }
        if (isBumped) bset.add(itemId);else bset.delete(itemId);
        recomputeBumpedTotals(tk);
        if (isBumped) {
          setTickets(prev => prev.map(t => t.ticket_instance_id === tk ? {
            ...t,
            anyCourseBumped: true
          } : t));
        }
      } else {
        setTickets(prev => prev.map(t => t.ticket_instance_id === tk ? {
          ...t,
          anyCourseBumped: true
        } : t));
      }
    };
    const onBumpCourse = p => {
      const tk = stableKeyFrom(p);
      const role = (p?.sender_role || "").toLowerCase();
      let courseNum = Number(p.course);
      if (!Number.isFinite(courseNum)) courseNum = null;
      if (!tk) return;
      ensureTicketSummary({
        ...p,
        ticket_instance_id: tk
      });
      if (role === "expo") {
        if (courseNum != null) markCourseReady(tk, courseNum, true);
      } else {
        setTickets(prev => prev.map(t => t.ticket_instance_id === tk ? {
          ...t,
          anyCourseBumped: true
        } : t));
      }
    };
    const onBumpTicket = p => {
      const tk = stableKeyFrom(p);
      const role = (p?.sender_role || "").toLowerCase();
      if (!tk) return;
      ensureTicketSummary({
        ...p,
        ticket_instance_id: tk
      });
      if (role === "expo") {
        const courseMap = itemsIndexRef.current.get(tk) || new Map();
        let bumpMap = bumpedIndexRef.current.get(tk);
        if (!bumpMap) {
          bumpMap = new Map();
          bumpedIndexRef.current.set(tk, bumpMap);
        }
        for (const [c, setIds] of courseMap.entries()) {
          let bset = bumpMap.get(c);
          if (!bset) {
            bset = new Set();
            bumpMap.set(c, bset);
          }
          for (const id of setIds) bset.add(id);
        }
        allItemsBumpedRef.current.set(tk, true);
        recomputeBumpedTotals(tk);
        setTickets(prev => prev.map(t => {
          if (t.ticket_instance_id !== tk) return t;
          const courseReady = {};
          for (const c of (itemsIndexRef.current.get(tk) || new Map()).keys()) {
            courseReady[Number(c)] = true;
          }
          return {
            ...t,
            courseReady,
            anyCourseBumped: true,
            readyToServe: Object.keys(courseReady).length > 0
          };
        }));
      } else {
        setTickets(prev => prev.map(t => t.ticket_instance_id === tk ? {
          ...t,
          anyCourseBumped: true
        } : t));
      }
    };

    // --- Authoritative + idempotent run-course receiver (match by order_id)
    // When a course gets "Ran by", treat it as picked up: remove from ready, add to served.
    const onRunCourse = p => {
      const orderId = p?.order_id != null ? String(p.order_id) : null;
      if (!orderId) return;
      const courseNum = Number(p.course);
      const runner = String(p.ran_by || p.server_name || "").trim();
      if (!Number.isFinite(courseNum) || !runner) return;

      // Ensure we have a summary (no-op if exists)
      ensureTicketSummary({
        order_id: orderId
      });

      // Apply: set Ran By only once, and mark that course as picked up.
      setTickets(prev => prev.map(t => {
        if (String(t.order_id) !== orderId) return t;
        const courseRanBy = {
          ...(t.courseRanBy || {})
        };
        const courseReady = {
          ...(t.courseReady || {})
        };
        const courseServed = {
          ...(t.courseServed || {})
        };

        // Record first runner only
        if (!courseRanBy[courseNum]) {
          courseRanBy[courseNum] = runner;
        }

        // If not already picked up, pick it up now
        if (!courseServed[courseNum]) {
          delete courseReady[courseNum];
          courseServed[courseNum] = true;
        }
        const totalCourses = Object.keys(t.courseTotals || {}).length;
        const servedCount = Object.keys(courseServed).filter(k => k in (t.courseTotals || {})).length;
        const fullyServed = totalCourses > 0 && servedCount >= totalCourses;
        return {
          ...t,
          courseRanBy,
          courseReady,
          courseServed,
          readyToServe: Object.keys(courseReady).length > 0 ? true : false,
          servedAt: fullyServed ? t.servedAt || new Date().toISOString() : t.servedAt
        };
      }));

      // schedule removal after pulse if fully served
      setTimeout(() => {
        setTickets(prev => {
          const cur = prev.find(x => String(x.order_id) === orderId);
          if (!cur || !cur.servedAt) return prev;
          const ticketKey = cur.ticket_instance_id;
          if (servedTimersRef.current.has(ticketKey)) {
            clearTimeout(servedTimersRef.current.get(ticketKey));
          }
          const to = setTimeout(() => {
            setTickets(p2 => p2.filter(x => x.ticket_instance_id !== ticketKey));
            servedTimersRef.current.delete(ticketKey);
          }, 5_000);
          servedTimersRef.current.set(ticketKey, to);
          return prev;
        });
      }, 0);
    };
    socket.on("connect", onConnect);
    socket.on("queue-screen:order-received", onOrderReceived);
    socket.on("queue-screen:item-received", onItemReceived);
    socket.on("queue-screen:bump-item", onBumpItem);
    socket.on("queue-screen:bump-course", onBumpCourse);
    socket.on("queue-screen:bump-ticket", onBumpTicket);
    socket.on("queue-screen:run-course", onRunCourse);
    if (socket.connected) onConnect();
    return () => {
      socket.off("connect", onConnect);
      socket.off("queue-screen:order-received", onOrderReceived);
      socket.off("queue-screen:item-received", onItemReceived);
      socket.off("queue-screen:bump-item", onBumpItem);
      socket.off("queue-screen:bump-course", onBumpCourse);
      socket.off("queue-screen:bump-ticket", onBumpTicket);
      socket.off("queue-screen:run-course", onRunCourse);
      for (const to of servedTimersRef.current.values()) clearTimeout(to);
      servedTimersRef.current.clear();
    };
  }, [expoStationId, sublocationId]);

  /** -------- detect newly ready courses and notify -------- */
  useEffect(() => {
    const curReady = new Map();
    for (const t of tickets) {
      if (!selectedServer) continue;
      if (selectedServer !== RUNNER_NAME && t.server_name !== selectedServer) continue;
      const ready = Object.keys(t.courseReady || {}).map(Number);
      if (ready.length) curReady.set(t.ticket_instance_id, new Set(ready));
    }
    for (const [tk, setNow] of curReady.entries()) {
      const setPrev = prevReadyMapRef.current.get(tk) || new Set();
      for (const c of setNow) {
        if (!setPrev.has(c)) {
          let seen = notifiedRef.current.get(tk);
          if (!seen) {
            seen = new Set();
            notifiedRef.current.set(tk, seen);
          }
          if (!seen.has(c)) {
            const ticket = tickets.find(x => x.ticket_instance_id === tk);
            const title = `Ready for pickup — ${ticket?.server_name || "Server"}`;
            const body = `Order #${ticket?.order_id ?? tk} • Course ${Number(c) + 1}${ticket?.table_name ? ` • Table ${ticket.table_name}` : ""}`;
            const tag = `ready-${tk}-${c}`;
            fireNotification({
              title,
              body,
              tag,
              data: {
                ticket: tk,
                course: c
              }
            });
            seen.add(c);
          }
        }
      }
    }
    prevReadyMapRef.current = curReady;
  }, [tickets, selectedServer]);

  // --- Double-tap handler (emit run-course once; idempotent)
  // Double-tap still emits, and we optimistically reflect. Server echo will reconcile.
  const lastTapRef = useRef(new Map());
  function handleCardClick(t) {
    const now = Date.now();
    const last = lastTapRef.current.get(t.ticket_instance_id) || 0;
    lastTapRef.current.set(t.ticket_instance_id, now);
    if (now - last < 300) {
      if (t.readyToServe) {
        const nextCourse = getNextReadyCourse(t);
        if (nextCourse != null) {
          const alreadyRan = !!(t.courseRanBy && t.courseRanBy[nextCourse]);
          if (!alreadyRan) {
            socket.emit("run-course", {
              order_id: t.order_id ?? null,
              ticket_instance_id: t.raw_ticket_instance_id ?? t.ticket_instance_id ?? null,
              course: nextCourse,
              // 0-based index
              course_label: `Course ${nextCourse + 1}`,
              ran_by: selectedServer || "Unknown",
              sender_screen_id: "mobile_queue_screen",
              sender_station_id: expoStationId ?? 1,
              sender_role: "expo",
              sublocation_id: sublocationId ? Number(sublocationId) : null,
              table_name: t.table_name ?? null,
              server_name: t.server_name ?? null,
              user_info: t.user_info ?? null,
              event_id: typeof window !== "undefined" && window.crypto?.randomUUID?.() || `mobile-queue-run-${String(t.order_id ?? t.ticket_instance_id ?? "unknown")}-${nextCourse}-${Date.now()}`,
              sentAt: new Date().toISOString()
            });

            // Optimistic local reflect: mark ran + pick up immediately
            setTickets(prev => prev.map(x => {
              if (x.ticket_instance_id !== t.ticket_instance_id) return x;
              const courseRanBy = {
                ...(x.courseRanBy || {})
              };
              const courseReady = {
                ...(x.courseReady || {})
              };
              const courseServed = {
                ...(x.courseServed || {})
              };
              courseRanBy[nextCourse] = selectedServer || "Unknown";
              delete courseReady[nextCourse];
              courseServed[nextCourse] = true;
              const totalCourses = Object.keys(x.courseTotals || {}).length;
              const servedCount = Object.keys(courseServed).filter(k => k in (x.courseTotals || {})).length;
              const fullyServed = totalCourses > 0 && servedCount >= totalCourses;
              return {
                ...x,
                courseRanBy,
                courseReady,
                courseServed,
                readyToServe: Object.keys(courseReady).length > 0,
                servedAt: fullyServed ? x.servedAt || new Date().toISOString() : x.servedAt
              };
            }));
          }
        }

        // Existing serve behavior (kept to preserve prior UX; harmless due to idempotency)
        markPickedUpOne(t.ticket_instance_id);
      }
    }
  }

  /** -------- Filter by selected server only -------- */
  const ticketsForServer = useMemo(() => {
    if (!selectedServer) return [];
    const pool = foodRunnerView || selectedServer === RUNNER_NAME ? tickets : tickets.filter(t => t.server_name === selectedServer);
    return pool.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [foodRunnerView, tickets, selectedServer]);
  return <div className="flex flex-col min-h-screen font-sans bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100">
            <style>{`
        @keyframes pulse-slow { 0%,100% { opacity: 1 } 50% { opacity: 0.55 } }
        .animate-pulse-slow { animation: pulse-slow 2.5s ease-in-out infinite; }
      `}</style>

            <header className="sticky top-0 z-20 px-3 pt-3">
                <div className="mx-auto max-w-6xl rounded-2xl border border-black/10 dark:border-white/10 bg-white/85 dark:bg-gray-800/85 backdrop-blur shadow-sm px-4 py-3 flex items-center justify-between gap-3">
                    <button type="button" onClick={() => setFoodRunnerView(prev => !prev)} aria-pressed={foodRunnerView} className={`inline-flex items-center rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${foodRunnerView ? "border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100"}`}>
                        Food Runner View {foodRunnerView ? "On" : "Off"}
                    </button>

                    <button onClick={toggleDarkMode} className="relative w-12 h-6 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center px-1 transition-colors duration-300">
                        <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform duration-300 ${isDark ? "translate-x-6" : "translate-x-0"}`} />
                        <span className="absolute left-1 text-xs">
                            <Icon icon={isDark ? "fluent-emoji:crescent-moon" : "fluent-emoji:sun"} className="h-4 w-4" aria-hidden="true" />
                        </span>
                    </button>
                </div>
            </header>

            <main className="flex-1 px-3 pb-6">
                {notifPermission !== "granted" && <div className="mb-3 p-3 rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-gray-800/70 text-sm flex items-center justify-between">
                        <span>Enable notifications to get alerted when your orders are ready.</span>
                        <button onClick={ensureNotificationPermission} className="ml-3 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                            Enable
                        </button>
                    </div>}

                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                    {ticketsForServer.map(t => {
          const st = statusOfTicket(t);
          const color = statusColors(st, isDark);
          const courses = courseList(t.courseTotals);
          const courseMap = itemsIndexRef.current.get(t.ticket_instance_id) || new Map();
          const bumpMap = bumpedIndexRef.current.get(t.ticket_instance_id) || new Map();
          const metaMap = itemsMetaRef.current.get(t.ticket_instance_id) || new Map();
          const courseReady = t.courseReady || {};
          const courseServed = t.courseServed || {};
          const courseRanBy = t.courseRanBy || {};
          const nextReady = Object.keys(courseReady).map(Number).sort((a, b) => a - b)[0];
          return <div key={t.ticket_instance_id} className={`mt-5 rounded-xl border shadow-sm hover:shadow-md transition-all duration-150 ${color} select-none`} style={{
            minHeight: 200
          }} onClick={() => handleCardClick(t)} role="group">
                                <div className="p-3 space-y-2 leading-snug">
                                    {/* header */}
                                    <div className="flex items-center justify-between text-xs opacity-70">
                                        <span><strong>Ticket #:</strong> {t.order_id}</span>
                                        <span>
                                            <strong>Received:</strong>{" "}
                                            {new Date(t.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                                        </span>
                                    </div>
                                    <div className="text-sm"><strong>Guest:</strong> {t.user_info || "—"}</div>
                                    <div className="text-sm"><strong>Table:</strong> {t.table_name || "—"}</div>
                                    {t.covers && <div className="text-sm"><strong>Covers:</strong> {t.covers}</div>}

                                    {/* Items cooked X / Y */}
                                    <div className="text-sm"><strong>Items Cooked:</strong> {t.bumpedCount ?? 0} / {t.totalItems ?? 0}</div>

                                    {/* Course - Items list */}
                                    {courses.length > 0 && <ul className="mt-2 space-y-2 text-[12px]">
                                            {courses.map(({
                  c
                }) => {
                  const ids = Array.from(courseMap.get(c) || []).sort();
                  const bumpedSet = bumpMap.get(c) || new Set();
                  const isReady = !!courseReady[c];
                  const isPickedUp = !!courseServed[c];
                  const ranByName = courseRanBy[c];
                  return <li key={`course-${t.ticket_instance_id}-${c}`} className="rounded bg-white/70 dark:bg-gray-800/60 px-2 py-2">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="font-medium">Course {c + 1}</span>
                                                                {isReady && !isPickedUp && <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-emerald-500/20 border border-emerald-600/40 text-emerald-900 dark:text-white">
                                                                        Ready for pickup
                                                                    </span>}
                                                                {isPickedUp && <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-blue-500/20 border border-blue-600/40 text-blue-900 dark:text-white">
                                                                        Picked up
                                                                    </span>}
                                                                {ranByName && <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-violet-500/20 border border-violet-600/40 text-violet-900 dark:text-white">
                                                                        Ran by: {ranByName}
                                                                    </span>}
                                                            </div>
                                                            <span className="text-[11px] opacity-70">{ids.length} item{ids.length === 1 ? "" : "s"}</span>
                                                        </div>

                                                        <div className="flex flex-col gap-1">
                                                            {ids.map(id => {
                        const meta = metaMap.get(id) || {};
                        const isBumped = bumpedSet.has(id);
                        return <div key={`item-${c}-${id}`} className={`flex items-center justify-between rounded border px-2 py-1 bg-white/80 dark:bg-gray-900/50 ${isBumped ? "opacity-70 line-through" : ""}`} title={`Course ${c + 1}${meta.seat_number ? ` • Seat ${meta.seat_number}` : ""}`}>
                                                                        <span className="truncate">{meta.name || id}</span>
                                                                        {meta.seat_number && <span className="ml-2 shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] border bg-slate-500/10 dark:bg-slate-300/10">
                                                                                Seat {meta.seat_number}
                                                                            </span>}
                                                                    </div>;
                      })}
                                                        </div>
                                                    </li>;
                })}
                                        </ul>}

                                    {/* optional small messages row */}
                                    {Array.isArray(t.ticketMessages) && t.ticketMessages.length > 0 && <div className="text-[11px] opacity-75">
                                            <strong>Msgs:</strong> {t.ticketMessages.slice(-2).join(" · ")}
                                        </div>}
                                </div>

                                <div className="grid grid-cols-2 text-center items-stretch border-t border-black/5 dark:border-white/10">
                                    <div className="px-2 py-2">
                                        <div className="text-xs font-semibold uppercase tracking-wide opacity-70">Status</div>
                                        <div className="text-sm font-medium">{statusOfTicket(t)}</div>
                                    </div>
                                    <div className="px-2 py-2">
                                        <div className="text-xs font-semibold uppercase tracking-wide opacity-70">Actions</div>
                                        <div className="pt-1">
                                            <span className="text-[11px] opacity-70">
                                                {t.readyToServe ? `Double-tap to serve ${typeof nextReady === 'number' ? `Course ${nextReady + 1}` : "course"}` : "Kitchen preparing…"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>;
        })}
                </div>

                {selectedServer && ticketsForServer.length === 0 && <div className="mt-10 text-center opacity-70">No tickets for {selectedServer} yet.</div>}
            </main>

            {/* --- FULLSCREEN SERVER PICKER --- */}
            {!selectedServer && <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                    <div className="relative max-w-sm w-[92vw] mx-auto rounded-2xl shadow-xl border border-black/10 dark:border-white/10 bg-white dark:bg-gray-900 p-5">
                        <h2 className="text-lg font-semibold mb-2">Select your server name</h2>
                        <p className="text-sm opacity-70 mb-4">
                            Pick your name to view only your tickets, or choose <strong>{RUNNER_NAME}</strong> to see all servers. You can change this later.
                        </p>

                        {serversLoading && <div className="py-6 text-center opacity-70">Loading servers…</div>}
                        {!serversLoading && serverError && <div className="py-4 text-red-600 dark:text-red-400 text-sm">{serverError}</div>}
                        {!serversLoading && !serverError && servers.length === 0 && <div className="py-6 text-center opacity-70">No servers found.</div>}

                        <div className="mt-3 mb-3 max-h-[50vh] overflow-y-auto flex flex-col gap-2 pr-1">
                            {servers.map(name => <button key={name} onClick={() => handlePickServer(name)} className="w-full text-left px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 active:scale-[0.99] transition">
                                    {name}
                                </button>)}
                        </div>
                    </div>
                </div>}

            {/* --- Small floating chip to switch server --- */}
            {selectedServer && <button className="fixed bottom-3 right-3 z-40 px-3 py-2 rounded-full text-xs border border-black/10 dark:border-white/10 bg-white/80 dark:bg-gray-800/80 backdrop-blur hover:bg-white dark:hover:bg-gray-800" onClick={() => {
      try {
        localStorage.removeItem("qs:selectedServer");
      } catch {}
      ;
      setSelectedServer("");
    }}>
                    Server: {selectedServer} • change
                </button>}
        </div>;
}
